import styled from 'styled-components';
import { PHONE_MEDIA_QUERY } from '../../../../common/responsive';
import { useNavigate } from 'react-router-dom';
import { hapticsImpactLight } from '../../../../common/haptic';
import VerifiedIcon from '../../../../assets/icons/verified.svg?react';
import GoldenIcon from '../../../../assets/icons/golden.svg?react';

const ScrollingContainer = styled.div`
  position: relative;
  width: 100%;
  background-color: transparent;
  overflow: hidden;
  z-index: 1;
  padding: 0;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100vw;
    height: 100%;
  }
`;

const InnerScroll = styled.div`
  overflow: hidden;
  white-space: nowrap;
  animation: ${(props) => (props.reverse ? 'scrollTextReverse' : 'scrollText')}
    120s infinite linear;
  margin: 0;
  font-size: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: fit-content;
  height: auto;

  @keyframes scrollText {
    from {
      transform: translateX(0%);
    }
    to {
      transform: translateX(-50%);
    }
  }

  @keyframes scrollTextReverse {
    from {
      transform: translateX(-50%);
    }
    to {
      transform: translateX(0%);
    }
  }
`;

const VerticalScroll = styled.div`
  overflow: hidden;
  animation: scrollVertical 120s infinite linear;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  height: fit-content;

  @keyframes scrollVertical {
    from {
      transform: translateY(0%);
    }
    to {
      transform: translateY(-50%);
    }
  }
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InnerContainer = styled.div`
  max-width: calc(1440px + 48px);
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 48px;
  padding-left: 24px;
  padding-right: 24px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 32px;
    max-width: unset;
    padding-left: 16px;
    padding-right: 16px;
    flex-direction: ${({ avoidDirectionChange }) =>
      avoidDirectionChange ? 'row' : 'column'};
  }
`;

const RowComponent = ({
  children,
  style = {},
  innerContainerStyle = {},
  avoidDirectionChange = false,
}) => {
  return (
    <Row style={style}>
      <InnerContainer
        style={innerContainerStyle}
        avoidDirectionChange={avoidDirectionChange}
      >
        {children}
      </InnerContainer>
    </Row>
  );
};

const ScrollItem = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  font-size: initial;
  align-items: stretch;
  height: auto;
`;

const VerticalScrollItem = styled.div`
  display: flex;
  flex-shrink: 0;
  font-size: initial;
  align-items: stretch;
  height: auto;
  width: 100%;
  justify-content: center;
`;

const HorizontalScroll = ({ tweets, reverse = false }) => {
  return (
    <ScrollingContainer>
      <InnerScroll reverse={reverse}>
        {tweets.map((icon, index) => (
          <ScrollItem key={`first-${index}`}>{icon}</ScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <ScrollItem key={`second-${index}`}>{icon}</ScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <ScrollItem key={`third-${index}`}>{icon}</ScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <ScrollItem key={`fourth-${index}`}>{icon}</ScrollItem>
        ))}
      </InnerScroll>
    </ScrollingContainer>
  );
};

const VerticalScrollComponent = ({ tweets }) => {
  return (
    <ScrollingContainer>
      <VerticalScroll>
        {tweets.map((icon, index) => (
          <VerticalScrollItem key={`first-${index}`}>{icon}</VerticalScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <VerticalScrollItem key={`second-${index}`}>
            {icon}
          </VerticalScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <VerticalScrollItem key={`third-${index}`}>{icon}</VerticalScrollItem>
        ))}
        {tweets.map((icon, index) => (
          <VerticalScrollItem key={`fourth-${index}`}>
            {icon}
          </VerticalScrollItem>
        ))}
      </VerticalScroll>
    </ScrollingContainer>
  );
};

const UserInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 8px;
`;

const UserImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: ${({ theme }) => theme.text.title};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserTag = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TweetContent = styled.div`
  width: 100%;
`;

const UserContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 300px;
  min-height: fit-content;
  height: auto;
  justify-content: flex-start;
  gap: 10px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.background.primary};
  color: ${({ theme }) => theme.text.title};
  margin-left: 10px;
  margin-right: 10px;
  font-size: initial;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
  cursor: pointer;

  p {
    width: 100%;
    margin: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }

  a {
    color: ${({ theme }) =>
      theme.primary?.main || theme.text.link || '#1DA1F2'};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media ${PHONE_MEDIA_QUERY} {
    width: calc(100% - 32px);
    max-width: 400px;
    margin-left: 0;
    margin-right: 0;
  }
`;

const Tweets = [
  {
    image:
      'https://pbs.twimg.com/profile_images/1977829045141188608/0uw29wzU_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        NOVA AI
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@NovaAI_Detector',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          The future of gifting is here 🤖
          <br />
          NOVA AI x{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          — exclusive IRL gift cards for our community ⚡️
          <br />
          See you at Breakpoint{' '}
          <a href='https://x.com/SolanaConf'>@SolanaConf</a> 🌟
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/NovaAI_Detector/status/1986867423970984353',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1996972184753909760/a9MWU3oT_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        bangerz
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@bangerz',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          thank u Magic Eden for gifting these at ur party
          <br />
          ppl didn't know what they had, they put them down on tables <br />
          i went around, collecting them, i now have 23
          <br />
          these are gift cards by{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          they contain crypto, specifically $ME
          <br />
          now i have gifts for my entire family, and i didn’t spend a dime
          <br />
          follow me for more financial lifestyle hacks
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/bangerz/status/2001387018220192090',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1839250075660300288/MqzvUQkq_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Caspar
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@casparstein',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          got gifted by{' '}
          <a
            href='https://x.com/NovaAI_Detector'
            target='_blank'
            rel='noopener noreferrer'
          >
            @NovaAI_Detector
          </a>
          <br />
          big thanks to{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/casparstein/status/1999423220840349770',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1983771872329183234/LH94Vxhf_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Jh0n.
      </div>
    ),
    tag: '@undercript0',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Los asistentes conocieron a{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>
          <br />y recibieron algunas tarjetas de <br />
          regalo... Deks es una solución increíble que resta fricciones y suma{' '}
          <br />
          bastante para un onboard más efectivo, un proyecto top Pronto sale su
          <br />
          video sobre ellos, atentos a las redes que habrán sorteos 🔥
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/undercript0/status/1980639836815127037',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1943750403381743616/AgGBQGP5_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Artur
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@Artur_RS_',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Feliz 4º aniversario @MonkeDAO 🧁🐵
          <br />
          ¡En Barcelona lo hemos celebrado a lo grande con la mejor vibra
          veraniega y fortaleciendo la comunidad que nunca!
          <br />
          Gracias a @LaFamilia_so por estar siempre presente y a{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>
          <br />
          holders 🎩💳 Recap ⬇️
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/undercript0/status/1980639836815127037',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1987667839956766720/2J0ooQGs_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Detroit Blockchain Collective
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@DetBlockchain',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          🎅🏽 We can’t wait to gift these to all the lucky winners at our Detroit
          Blockchain Collective holiday party tonight! 🎊Thank you{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          for making our Christmas party extra special & helping us bless our
          incredible community members!🎄🎁
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/DetBlockchain/status/1999571726590509222',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1986093064507523072/JluP3qUs_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Hanami | foresee.lol
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@hanami_foresee',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          has the cleanest UI in crypto.
          <br />
          if i had to onboard my grandma tomorrow, i'd start with a deks gift
          card.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/hanami_foresee/status/1993038589421367582',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1895235632059863040/MOK9kHch_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Marco Sola
      </div>
    ),
    tag: '@MarkushiSola',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          🎁 Un gran ejemplo es{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          , ganador de uno de los últimos hackathons: la forma más fácil de
          <br />
          regalar crypto y acercar a más personas al mundo blockchain.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/MarkushiSola/status/1976749479081419157',
  },
];

const TweetsReverse = [
  {
    image:
      'https://pbs.twimg.com/profile_images/1898392761251848192/Sxd-mBvH_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Hüseyin GÜLYAZ
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@gulyazzhuseyin',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Gifting crypto finally makes sense.{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          turns any Solana asset into a shareable gift card sent by email, SMS
          or even printed. No wallet setup, no friction, no “how do I use this?”
          moments.
          <br />
          It's the easiest onboarding tool disguised as a present. Simple for
          newcomers, powerful for pros.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/gulyazzhuseyin/status/1996295817259303266',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1864046290511769600/VxGFL-NX_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        MIDA
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@Mida_DAO',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Something special in the making
          <br /> 👀 MIDA x{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          — custom gift cards coming for our community at @SolanaConf ⚡️
          <br />
          See you there. 🏛️
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/Mida_DAO/status/1983626994568065432',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1984974054751645696/3a1sRwdt_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Kevin Webster
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@kwebthree',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Been using{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          to pay{' '}
          <a
            href='https://x.com/lautaroweb3'
            target='_blank'
            rel='noopener noreferrer'
          >
            @lautaroweb3
          </a>{' '}
          for everything on this trip.
          <br />
          Something fun about having a photo log of the trip through our
          payments (need some copy updates for this use case lol)
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/kwebthree/status/1984721580375564634',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1990118371824939008/eGoxiybX_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        0xyuki.sol ☂️ | Subly
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@0xyuki3',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          Final week of Colosseum Cypherpunk Hackathon.
          <br />
          Just got a @xStocksFi Netflix token gift card to cheer me🔥
          <br />
          (Just kidding lol)
          <br /> I tried{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          for the first time. It’s super cool.
          <br /> I can easily customize the card image and add my own message.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/0xyuki3/status/1981370080828280917',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1923067389052084224/FGBEHBAj_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Magic Eden en Español
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@Eden_Magico',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          gME 🪄
          <br />
          Anunciamos un partnership con{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          para impulsar la comunidad de Magic Eden en Español.
          <br />
          Deks nos brindará tarjetas de regalo personalizadas de $ME para
          distribuir en eventos, conferencias y sorteos digitales, impulsando el
          ecosistema de Magic Eden en la comunidad hispana.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/Eden_Magico/status/1976374239726055601',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1995860224998014976/cLgPd-C8_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Vitoria
        <VerifiedIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@vitoriagoodies',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          just sent my boyfriend an onchain gift card using{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          , with our face on it
          <br />
          😍 this was his actual reaction:
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/Eden_Magico/status/1976374239726055601',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1901668683665330176/A2VaXeqQ_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        La Familia
        <GoldenIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@LaFamilia_so',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          La forma mas sencilla de enviar cualquier token es usando{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/LaFamilia_so/status/1986500773686001672',
  },
  {
    image:
      'https://pbs.twimg.com/profile_images/1965878160282566656/_nX_ra42_400x400.jpg',
    name: (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
        }}
      >
        Strategic Super Reserve SSR
        <GoldenIcon
          width={16}
          height={16}
          color={'rgb(29, 155, 240)'}
          style={{
            color: 'rgb(29, 155, 240)',
          }}
        />
      </div>
    ),
    tag: '@StrategicSuperR',
    content: (
      <div style={{ width: '100%' }}>
        <p>
          $SSR's @kujcrypto putting in the bagwork with the $BAGWORK legends at
          Token2049 🔥
          <br /> Brand new physical $SSR giftcards being handed out, courtesy of
          our partners{' '}
          <a
            href='https://x.com/deksxyz'
            target='_blank'
            rel='noopener noreferrer'
          >
            @deksxyz
          </a>{' '}
          , so we can spread Eagleitarianism to every corner of the ecosystem.
          <br />
          Pumptober is now officially in full effect ladies and gents.
        </p>
      </div>
    ),
    tweetUrl: 'https://x.com/StrategicSuperR/status/1973794979181887502',
  },
];

const Container = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  position: relative;

  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    height: 90vh;
    max-height: 800px;
    overflow: hidden;
  }
`;

const MobileScrollWrapper = styled.div`
  display: none;
  width: 100%;
  height: 100%;

  @media ${PHONE_MEDIA_QUERY} {
    display: block;
  }
`;

const DesktopScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${PHONE_MEDIA_QUERY} {
    display: none;
  }
`;

const DissapearSoftener = styled.div`
  pointer-events: none;
  height: 100%;
  position: absolute;
  ${(props) => (props.reverse ? 'right: 0px;' : 'left: 0px;')};
  width: 150px;
  z-index: 3;
  background: ${(props) =>
    props.reverse
      ? `linear-gradient(90deg, rgba(${props.theme.primary.backgroundRgb}, 0) 0%, rgba(${props.theme.primary.backgroundRgb}, 1) 80%)`
      : `linear-gradient(90deg, rgba(${props.theme.primary.backgroundRgb}, 1) 20%, rgba(${props.theme.primary.backgroundRgb}, 0) 100%)`};

  @media ${PHONE_MEDIA_QUERY} {
    display: none;
  }
`;

const DissapearSoftenerVertical = styled.div`
  pointer-events: none;
  display: none;
  position: absolute;
  height: 150px;
  width: 100%;
  z-index: 3;
  ${(props) =>
    props.reverse ? 'bottom: 0px; top: auto;' : 'top: 0px; bottom: auto;'};
  background: ${(props) =>
    props.reverse
      ? `linear-gradient(180deg, rgba(${props.theme.primary.backgroundRgb}, 0) 0%, rgba(${props.theme.primary.backgroundRgb}, 1) 80%)`
      : `linear-gradient(180deg, rgba(${props.theme.primary.backgroundRgb}, 1) 20%, rgba(${props.theme.primary.backgroundRgb}, 0) 100%)`};

  @media ${PHONE_MEDIA_QUERY} {
    display: block;
  }
`;

const GiftCardsScroll = () => {
  const navigate = useNavigate();

  const mixedTweets = [...Tweets, ...TweetsReverse];

  const createTweetComponent = (tweet, index, prefix = '') => (
    <UserContainer
      key={`${prefix}-${index}`}
      onClick={() => {
        hapticsImpactLight();
        window.open(tweet.tweetUrl, '_blank');
      }}
    >
      <UserInfo>
        <UserImage src={tweet.image} alt={tweet.name} />
        <UserDetails>
          <UserName>{tweet.name}</UserName>
          <UserTag>{tweet.tag}</UserTag>
        </UserDetails>
      </UserInfo>
      <TweetContent>{tweet.content}</TweetContent>
    </UserContainer>
  );

  return (
    <Container>
      <DissapearSoftener reverse={false} />
      <DissapearSoftener reverse={true} />

      <DissapearSoftenerVertical reverse={false} />
      <DissapearSoftenerVertical reverse={true} />

      <MobileScrollWrapper>
        <VerticalScrollComponent
          tweets={mixedTweets.map((tweet, index) =>
            createTweetComponent(tweet, index, 'mobile'),
          )}
        />
      </MobileScrollWrapper>

      <DesktopScrollWrapper>
        <RowComponent>
          <HorizontalScroll
            tweets={Tweets.map((tweet, index) =>
              createTweetComponent(tweet, index, 'desktop-first'),
            )}
          />
        </RowComponent>
        <RowComponent>
          <HorizontalScroll
            tweets={TweetsReverse.map((tweet, index) =>
              createTweetComponent(tweet, index, 'desktop-second'),
            )}
            reverse={true}
          />
        </RowComponent>
      </DesktopScrollWrapper>
    </Container>
  );
};

export default GiftCardsScroll;
